import React from 'react';
import { Checkbox, CheckboxWithLabel } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';

import * as styles from './styles';

type CheckboxData = {
  checked?: boolean;
  disabled?: boolean;
  href?: string;
  labelText: string;
  secondaryLabelText?: string;
  value: string;
};

export interface CheckboxGroupProps {
  accessibleLabel: string;
  accessibleLabelElementId?: string;
  accessibleLabelElementType?: keyof JSX.IntrinsicElements;
  checkboxData: CheckboxData[];
  hideLabel?: boolean;
  name: string;
  onChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
}

export const CheckboxGroup = ({
  name,
  accessibleLabel,
  accessibleLabelElementType: AccessibleLabelElementTag = 'p',
  accessibleLabelElementId = `${name}-label`,
  onChange,
  checkboxData,
  hideLabel,
}: CheckboxGroupProps) => {
  const handleUnderlayLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const label = event.currentTarget.closest('label');
    const checkbox = label?.querySelector<HTMLInputElement>('input[type="checkbox"]');
    checkbox?.click();
  };

  return (
    <div aria-labelledby={accessibleLabelElementId} role="group">
      <span css={hideLabel ? visuallyHidden : undefined} id={accessibleLabelElementId}>
        <AccessibleLabelElementTag>{accessibleLabel}</AccessibleLabelElementTag>
      </span>
      {checkboxData.map(
        ({ checked, disabled, href, labelText, secondaryLabelText, value: checkboxValue }) => {
          const showLink = Boolean(href && !disabled);
          const checkboxInputId = `${name}-${checkboxValue}`;

          const checkboxRow = showLink ? (
            <label
              css={[
                styles.checkboxLabelBase,
                disabled && styles.checkboxLabelDisabledState,
                styles.checkboxWrapper,
              ]}
              htmlFor={checkboxInputId}
            >
              <a
                aria-hidden="true"
                css={styles.linkUnderlay}
                data-testid={`${checkboxValue}-link`}
                href={href}
                onClick={handleUnderlayLinkClick}
                rel="nofollow"
                tabIndex={-1}
              >
                <span css={visuallyHidden}>{labelText}</span>
              </a>
              <span css={styles.checkboxLabelForeground}>
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  id={checkboxInputId}
                  name={name}
                  onChange={onChange}
                  value={checkboxValue}
                />
                <span
                  css={styles.checkboxLabelTextSpan}
                  dangerouslySetInnerHTML={{ __html: labelText }}
                />
              </span>
            </label>
          ) : (
            <CheckboxWithLabel
              checked={checked}
              css={styles.checkboxWrapper}
              disabled={disabled}
              label={labelText}
              name={name}
              onChange={onChange}
              value={checkboxValue}
            />
          );

          return (
            <div
              css={styles.checkboxWrapperContainer}
              data-testid={checkboxValue}
              key={checkboxValue}
            >
              {checkboxRow}
              {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
            </div>
          );
        },
      )}
    </div>
  );
};
